// LiveView — browser-side VNC viewer for a live job. The backend never
// proxies pixels; we fetch a short-lived ticket from
//   POST /v1/jobs/{jobId}/session-ticket?mode=view|control
// then open a WebSocket straight to the owning fleet host's /vnc
// endpoint. noVNC renders the raw RFB stream to a canvas.
//
//   <LiveView jobId jobState canControl? />
//
// mode=view is anonymous-allowed; mode=control needs an authed session
// AND user_id === job.submitter_user_id. The submitter check happens
// server-side — the frontend simply offers the Take-control tab when
// the viewer is signed in and surfaces the 401/403 as an inline note.
import { useEffect, useRef, useState } from "react";
import RFB from "@novnc/novnc";
import { api, ApiError, isApiError } from "@/lib/api";

type Mode = "view" | "control";
type Status = "connecting" | "connected" | "disconnected" | "rejected";

type Props = {
  jobId: string;
  jobState?: string;
  canControl?: boolean;
  className?: string;
};

export function LiveView({ jobId, canControl = false, className }: Props) {
  const [mode, setMode] = useState<Mode>("view");
  const [status, setStatus] = useState<Status>("connecting");
  const [note, setNote] = useState<string>("");
  const hostRef = useRef<HTMLDivElement | null>(null);
  const rfbRef = useRef<RFB | null>(null);

  useEffect(() => {
    let cancelled = false;
    let reconnectT: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      setStatus("connecting");
      setNote("");
      let ticket;
      try {
        ticket = await api.sessionTicket(jobId, mode);
      } catch (e) {
        if (cancelled) return;
        setStatus("rejected");
        setNote(reasonFor(e, mode));
        return;
      }
      if (cancelled || !hostRef.current) return;
      // noVNC accepts the ticket as a URL query (passes through to the
      // controller, which parses it). Credentials are irrelevant to the
      // RFB handshake because our proxy auths with the ticket not a
      // VNC password.
      const url = `${ticket.host_url}/vnc/${encodeURIComponent(jobId)}?ticket=${encodeURIComponent(ticket.ticket)}`;
      let rfb: RFB;
      try {
        rfb = new RFB(hostRef.current, url, {
          wsProtocols: ["binary"],
        });
      } catch (e) {
        setStatus("rejected");
        setNote(e instanceof Error ? e.message : String(e));
        return;
      }
      rfb.viewOnly = mode !== "control";
      rfb.scaleViewport = true;
      rfb.resizeSession = false;
      rfb.background = "transparent";
      rfb.addEventListener("connect", () => {
        if (!cancelled) setStatus("connected");
      });
      rfb.addEventListener("disconnect", () => {
        if (cancelled) return;
        setStatus("disconnected");
        // The ticket is single-use for 60 s; a 401/403 here means the
        // ticket expired. Reconnect with a fresh one after a short beat.
        if (reconnectT) clearTimeout(reconnectT);
        reconnectT = setTimeout(() => {
          rfbRef.current = null;
          connect();
        }, 1500);
      });
      rfbRef.current = rfb;
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectT) clearTimeout(reconnectT);
      if (rfbRef.current) {
        try {
          rfbRef.current.disconnect();
        } catch {
          /* noVNC throws when already closed; ignore */
        }
        rfbRef.current = null;
      }
      // noVNC injects a canvas we don't own; wipe the host so the next
      // mount starts clean.
      if (hostRef.current) hostRef.current.replaceChildren();
    };
  }, [jobId, mode]);

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
  };

  return (
    // w-full so Plate's inner `.plate-field` (display: grid; place-items:
    // center) doesn't collapse us to our content width. The 16:10 frame
    // then takes the full plate interior.
    <div className={`w-full ${className ?? ""}`}>
      <div className="flex items-center justify-between gap-2 border-b border-ink-faint pb-1.5">
        <div className="flex gap-1" role="tablist">
          <TabButton active={mode === "view"} onClick={() => switchMode("view")} label="Live view" />
          {canControl && <TabButton active={mode === "control"} onClick={() => switchMode("control")} label="Take control" />}
        </div>
        <StatusChip status={status} />
      </div>
      <div className="relative w-full overflow-hidden bg-sunk" style={{ aspectRatio: "16 / 10" }}>
        <div ref={hostRef} className="absolute inset-0" />
        {status !== "connected" && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <div className="font-mono text-key uppercase text-ink-mute">
              {status === "rejected" ? note || "Live view unavailable" : status === "disconnected" ? "Disconnected — reconnecting" : "Connecting"}
            </div>
          </div>
        )}
      </div>
      {status === "rejected" && note && (
        <p className="meta mt-1.5 text-ink-mute" aria-live="polite">
          {note}
        </p>
      )}
    </div>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`px-2.5 py-1 font-mono text-key uppercase ${active ? "text-ink" : "text-ink-mute hover:text-ink"}`}
    >
      {label}
    </button>
  );
}

function StatusChip({ status }: { status: Status }) {
  const map: Record<Status, string> = {
    connecting: "connecting",
    connected: "live",
    disconnected: "reconnecting",
    rejected: "unavailable",
  };
  return <span className="font-mono text-meta uppercase text-ink-mute">{map[status]}</span>;
}

function reasonFor(e: unknown, mode: Mode): string {
  if (isApiError(e)) {
    const err = e as ApiError;
    if (err.status === 401) return mode === "control" ? "Sign in to take control of this analysis." : "Not available.";
    if (err.status === 403) return mode === "control" ? "Only the submitter may take control of this analysis." : "Not available.";
    if (err.status === 409) return "Job isn't live yet.";
    if (err.status === 503) return "Live view not configured on this deployment.";
    return err.detail || `Backend returned ${err.status}.`;
  }
  return e instanceof Error ? e.message : "Live view unavailable.";
}
